import { ItemTypes } from '../../constants/constants';
import { boxMatchesStyle } from '../boxes/styleTemplate';
import { findProposalTableStyle } from '../tables/proposalTableStyle';
import { defaultThemeStyle, themeStylesOf } from './themeStyles';
import { readThemeConfiguration } from './themeConfiguration';

/**
 * Correspondance entre les styles du document et ceux du thème du site, à la
 * génération : « les tableaux de style Bleu du document prennent le style
 * Rouge du site ».
 *
 * La clé d'un style du document est son identifiant du configurateur. Les
 * cartes info et les volets n'en référencent pas (le style est recopié dans la
 * box) : leur style se déduit des valeurs, et ceux qui n'en suivent aucun
 * partagent la clé `CUSTOM_STYLE_KEY`.
 */

export const CUSTOM_STYLE_KEY = 'custom';

/**
 * Les composants qu'un document peut porter : seuls leurs styles se font
 * correspondre à la génération. Les autres n'existent que sur le site.
 */
export const DOCUMENT_STYLE_KINDS = ['table', 'infoCard', 'accordion'];

/**
 * Réponse « garder le style du document » : il est importé dans le thème du
 * site. Mémorisée telle quelle, pour être redonnée à la régénération. Pour les
 * cartes et volets personnalisés, garder leur style s'écrit `null` (rien à
 * importer, ils gardent leurs valeurs).
 */
export const DOCUMENT_STYLE = 'document';

const DEFAULT_STYLE_KEY = 'default';

function tableStyleEntry(box, configuration) {
  // Un tableau sans style suit le premier, comme dans le document.
  const style = findProposalTableStyle(box.tableStyleId, configuration)
    || configuration?.tableStyles?.[0];
  if (!style) return { key: DEFAULT_STYLE_KEY, name: 'Défaut', style: null };
  return { key: String(style.id), name: style.name, style };
}

// Box dont le style est recopié (pas référencé) : il se déduit des valeurs.
function deducedStyleEntry(values, styles, customName) {
  const style = (styles || []).find((candidate) => boxMatchesStyle(values, candidate));
  if (!style) return { key: CUSTOM_STYLE_KEY, name: customName, style: null };
  return { key: String(style.id), name: style.name, style };
}

function entryOf(box, configuration) {
  const c = configuration || {};
  switch (box.type) {
    case ItemTypes.TABLE:
    case ItemTypes.TABLE_VARIABLE:
      return { kind: 'table', ...tableStyleEntry(box, c) };
    case ItemTypes.INFO_CARD:
      return { kind: 'infoCard', ...deducedStyleEntry(box.content?.card, c.infoCardStyles, 'Cartes personnalisées') };
    case ItemTypes.ACCORDION_PANEL:
      return { kind: 'accordion', ...deducedStyleEntry(box.content?.panel, c.accordionPanelStyles, 'Volets personnalisés') };
    default:
      return null;
  }
}

/**
 * Les styles du document réellement utilisés, par type de composant, et le
 * style de chaque box concernée.
 *
 * @returns {{
 *   entries: Record<string, { key, name, count, style }[]>,
 *   boxKeys: Record<string, { kind: string, key: string }>,
 * }}
 */
export function collectUsedProposalStyles({ boxes = [], configuration = {} } = {}) {
  const entries = Object.fromEntries(DOCUMENT_STYLE_KINDS.map((kind) => [kind, []]));
  const boxKeys = {};
  boxes.forEach((box) => {
    const entry = entryOf(box, configuration);
    if (!entry) return;
    const { kind, key, name, style } = entry;
    boxKeys[box.id] = { kind, key };
    const existing = entries[kind].find((e) => e.key === key);
    if (existing) existing.count += 1;
    else entries[kind].push({ key, name, count: 1, style });
  });
  return { entries, boxKeys };
}

const normalizeName = (name) => String(name || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .trim()
  .toLowerCase();

function suggestedWebStyleId(entry, webStyles, defaultStyle) {
  const sameName = webStyles.find((style) => normalizeName(style.name) === normalizeName(entry.name));
  return (sameName || defaultStyle || webStyles[0]).id;
}

/**
 * Plan pour un type de composant.
 *
 * - aucun style dans le thème : les styles du document sont importés ;
 * - un seul : tout le monde le prend, sans rien demander ;
 * - plusieurs : une question par style du document, pré-remplie par le choix
 *   mémorisé, sinon le style de même nom, sinon le Défaut épinglé du thème
 *   (`defaultStyle`). Un choix mémorisé
 *   dont la cible existe encore n'est pas redemandé.
 *
 * Les cartes personnalisées ne sont jamais importées : par défaut elles
 * restent telles quelles (`null`).
 *
 * @returns {{ mapping: Record<string, number|null>, questions: string[], imports: string[] }}
 */
export function planKindMapping(entries, webStyles, previous = {}, defaultStyle = null) {
  const mapping = {};
  const questions = [];
  const imports = [];
  const isWebStyle = (id) => webStyles.some((style) => String(style.id) === String(id));

  entries.forEach((entry) => {
    const isCustom = entry.key === CUSTOM_STYLE_KEY;
    if (!webStyles.length) {
      mapping[entry.key] = null;
      if (!isCustom) imports.push(entry.key);
      return;
    }
    const remembered = previous?.[entry.key];
    // `null` mémorisé ne vaut choix que pour les cartes personnalisées : pour
    // un style importé faute de thème, ce n'était pas un choix.
    if ((remembered === null && isCustom)
      || (remembered === DOCUMENT_STYLE && !isCustom)
      || (remembered != null && isWebStyle(remembered))) {
      mapping[entry.key] = remembered;
      return;
    }
    if (webStyles.length === 1) {
      mapping[entry.key] = isCustom ? null : webStyles[0].id;
      return;
    }
    mapping[entry.key] = isCustom ? null : suggestedWebStyleId(entry, webStyles, defaultStyle);
    questions.push(entry.key);
  });

  return { mapping, questions, imports };
}

/** Le plan de tous les types de composant pour un document et un thème. */
export function planThemeStyleMapping({ used, theme, previousMapping = {} }) {
  const configuration = readThemeConfiguration(theme);
  return Object.fromEntries(DOCUMENT_STYLE_KINDS.map((kind) => [
    kind,
    planKindMapping(
      used.entries[kind] || [],
      themeStylesOf(configuration, kind),
      previousMapping?.[kind],
      defaultThemeStyle(configuration, kind),
    ),
  ]));
}

/**
 * Le thème du site EST la configuration du document (template jamais
 * configuré) : chaque style du document est le sien. Rien à demander.
 */
export function identityStyleMapping(used) {
  return Object.fromEntries(DOCUMENT_STYLE_KINDS.map((kind) => [
    kind,
    Object.fromEntries((used.entries[kind] || []).map((entry) => [
      entry.key,
      entry.key === CUSTOM_STYLE_KEY || !entry.style ? null : entry.style.id,
    ])),
  ]));
}

export function hasStyleMappingQuestions(plan) {
  return Object.values(plan || {}).some((kindPlan) => kindPlan.questions.length > 0);
}

/**
 * Y a-t-il au moins un style du document à associer à un style du site, même
 * déjà réglé (choix mémorisé, même nom) ? Les styles importés faute de style
 * du site n'ont pas de choix.
 */
export function hasStyleMappingChoices(plan) {
  return Object.values(plan || {}).some((kindPlan) => Object.keys(kindPlan.mapping)
    .some((key) => !kindPlan.imports.includes(key)));
}
